/**
 * FauxDS LoadingSpinner
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate LoadingSpinner component for prototyping.
 * It is NOT the real @faire/slate LoadingSpinner and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate LoadingSpinner, see: @faire/slate/components/loading-spinner
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

export type SpinnerSize = "xSmall" | "small" | "medium" | "large" | "xLarge";

export interface LoadingSpinnerProps {
  /**
   * The size of the spinner
   * - xSmall: 12px
   * - small: 16px
   * - medium: 24px
   * - large: 32px
   * - xLarge: 48px
   * @default "small"
   */
  size?: SpinnerSize;
  /**
   * Accessible label for the spinner
   * @default "Loading"
   */
  "aria-label"?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Size to pixel mapping
const sizeMap: Record<SpinnerSize, number> = {
  xSmall: 12,
  small: 16,
  medium: 24,
  large: 32,
  xLarge: 48,
};

// Size to stroke width mapping
const strokeWidthMap: Record<SpinnerSize, number> = {
  xSmall: 4.5,
  small: 3.5,
  medium: 2.5,
  large: 2,
  xLarge: 1.5,
};

/**
 * LoadingSpinner - FauxDS (recreated from Slate)
 *
 * A loading spinner represents an unspecified loading time.
 * This is a simplified implementation for prototyping purposes.
 */
export default function LoadingSpinner({
  size = "small",
  "aria-label": ariaLabel = "Loading",
  className,
}: LoadingSpinnerProps) {
  const pixelSize = sizeMap[size];
  const strokeWidth = strokeWidthMap[size];

  return (
    <svg
      className={cn(
        "animate-spin text-(--slate-color-text-primary)",
        className
      )}
      viewBox="25 25 50 50"
      width={pixelSize}
      height={pixelSize}
      role="status"
      aria-label={ariaLabel}
      style={{
        animationDuration: "0.77s",
        transformOrigin: "center",
      }}
    >
      <circle
        cx="50"
        cy="50"
        r="20"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeDasharray="95"
        strokeDashoffset="0"
      />
    </svg>
  );
}
